import { useCallback } from "react";
import { Route } from "../../../../lib/routes";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { FeatureFlag, isPosthogFeatureFlagEnabled } from "lib/analytics/featureFlags";
import { IS_LOCAL_DEVELOPMENT, IS_TEST } from "../../../../lib/config";
import useRouteAccess from "../../../../lib/useRouteAccess";
import { PricingFeatures } from "components/companies/pricingFeatures";
import { useCompanyPricingFeatures } from "components/companies/hooks/useCompanyPricingFeatures";
import { LinkDisplayStatus } from "components/layout/private/SidebarV2/linkDisplayStatus";

// todo: when we become comfortable with this logic, consolidate
// with the SidebarV2.tsx file conditionals

export const useLinkDisplay = () => {
  const { hasAccess } = useRouteAccess();
  const { company } = useActiveCompany<true>();
  const { pricePlanFeatureEnabled } = useCompanyPricingFeatures(company);

  const getLinkDisplayStatus = useCallback(
    (path: Route): LinkDisplayStatus => {
      // Check PostHog feature flags first
      // Typically if something is still behind a feature flag,
      // It will be completely hidden

      if (path === Route.customers && !isPosthogFeatureFlagEnabled(FeatureFlag.CustomersPage)) {
        return LinkDisplayStatus.POSTHOG_FEATURE_FLAGGED;
      }

      if (path === Route.products && !isPosthogFeatureFlagEnabled(FeatureFlag.ProductsPage)) {
        return LinkDisplayStatus.POSTHOG_FEATURE_FLAGGED;
      }

      if (path === Route.aiLabs && !company.features.aiInsightsEnabled) {
        return LinkDisplayStatus.COMPANY_FEATURE_FLAGGED;
      }

      if (
        path === Route.newInvoicePayment &&
        !company.features.arPaymentsEnabled &&
        (!IS_LOCAL_DEVELOPMENT || IS_TEST)
      ) {
        return LinkDisplayStatus.COMPANY_FEATURE_FLAGGED;
      }

      if (
        path === Route.newBillPayment &&
        !company.features.apPaymentsEnabled &&
        (!IS_LOCAL_DEVELOPMENT || IS_TEST)
      ) {
        return LinkDisplayStatus.COMPANY_FEATURE_FLAGGED;
      }

      // Check pricing plan features
      if (
        path === Route.spending &&
        !pricePlanFeatureEnabled.has(PricingFeatures.spend_explorer_vendor_insights)
      ) {
        return LinkDisplayStatus.FEATURE_GATED;
      }

      if (path === Route.people && !pricePlanFeatureEnabled.has(PricingFeatures.people_insights)) {
        return LinkDisplayStatus.FEATURE_GATED;
      }

      // Then check permissions
      if (!hasAccess(path)) {
        return LinkDisplayStatus.NO_PERMISSIONS;
      }

      return LinkDisplayStatus.SHOW;
    },
    [hasAccess, company, pricePlanFeatureEnabled]
  );

  const shouldShowLink = useCallback(
    (path: Route): boolean => {
      return getLinkDisplayStatus(path) === LinkDisplayStatus.SHOW;
    },
    [getLinkDisplayStatus]
  );

  return {
    shouldShowLink,
    getLinkDisplayStatus,
  };
};

export default useLinkDisplay;
